import { useState, useEffect, useCallback, useRef } from 'react';
import performanceManager from '../utils/performanceManager';

/**
 * Hook for using the site-wide performance manager
 */
export const usePerformanceManager = (componentId) => {
  const [loadingState, setLoadingState] = useState(null);
  const [error, setError] = useState(null);
  const [memoryStatus, setMemoryStatus] = useState('optimal');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const componentIdRef = useRef(componentId || `component_${Date.now()}_${Math.random()}`);

  useEffect(() => {
    const id = componentIdRef.current;

    // Event listeners for performance events
    const handleLoadingStart = (event) => {
      if (event.detail.id === id) {
        setLoadingState(event.detail);
      }
    };

    const handleLoadingProgress = (event) => {
      if (event.detail.id === id) {
        setLoadingState(event.detail);
      }
    };

    const handleLoadingEnd = (event) => {
      if (event.detail.id === id) {
        setLoadingState(event.detail);
      }
    };

    const handleErrorTracked = (event) => {
      if (event.detail.id === id) {
        setError(event.detail);
      }
    };

    const handleErrorCleared = (event) => {
      if (event.detail.id === id) {
        setError(null);
      }
    };

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    const handleMemoryOptimized = () => {
      setMemoryStatus(performanceManager.isMemoryOptimal() ? 'optimal' : 'high');
    };

    // Add event listeners
    window.addEventListener('performance-loading-start', handleLoadingStart);
    window.addEventListener('performance-loading-progress', handleLoadingProgress);
    window.addEventListener('performance-loading-end', handleLoadingEnd);
    window.addEventListener('performance-error-tracked', handleErrorTracked);
    window.addEventListener('performance-error-cleared', handleErrorCleared);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('memory-optimized', handleMemoryOptimized);

    // Initial state
    setLoadingState(performanceManager?.getLoadingState?.(id) || null);
    setError(performanceManager?.getError?.(id) || null);
    setMemoryStatus(performanceManager?.isMemoryOptimal?.() ? 'optimal' : 'high');

    return () => {
      // Cleanup event listeners
      window.removeEventListener('performance-loading-start', handleLoadingStart);
      window.removeEventListener('performance-loading-progress', handleLoadingProgress);
      window.removeEventListener('performance-loading-end', handleLoadingEnd);
      window.removeEventListener('performance-error-tracked', handleErrorTracked);
      window.removeEventListener('performance-error-cleared', handleErrorCleared);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('memory-optimized', handleMemoryOptimized);
    };
  }, []);

  // Loading management functions
  const startLoading = useCallback((dataSize = 0) => {
    return performanceManager?.trackLoadingStart?.(componentIdRef.current, dataSize);
  }, []);

  const updateProgress = useCallback((progress, message = '') => {
    if (performanceManager?.updateLoadingProgress) {
      performanceManager.updateLoadingProgress(componentIdRef.current, progress, message);
    }
  }, []);

  const endLoading = useCallback((success = true, error = null) => {
    if (performanceManager?.trackLoadingEnd) {
      performanceManager.trackLoadingEnd(componentIdRef.current, success, error);
    }
  }, []);

  // Error management functions
  const trackError = useCallback((error, context = {}) => {
    return performanceManager?.trackError?.(componentIdRef.current, error, context);
  }, []);

  const clearError = useCallback(() => {
    return performanceManager?.clearError?.(componentIdRef.current);
  }, []);

  // Cache management functions
  const cacheData = useCallback((key, data, ttl) => {
    if (performanceManager?.cacheData) {
      performanceManager.cacheData(`${componentIdRef.current}_${key}`, data, ttl);
    }
  }, []);

  const getCachedData = useCallback((key) => {
    return performanceManager?.getCachedData?.(`${componentIdRef.current}_${key}`);
  }, []);

  // Offline sync functions
  const queueForSync = useCallback((operation) => {
    if (performanceManager?.queueForSync) {
      performanceManager.queueForSync({
        ...operation,
        componentId: componentIdRef.current
      });
    }
  }, []);

  return {
    // State
    loadingState,
    error,
    memoryStatus,
    isOnline,
    componentId: componentIdRef.current,

    // Loading functions
    startLoading,
    updateProgress,
    endLoading,
    isLoading: loadingState?.isLoading || false,

    // Error functions
    trackError,
    clearError,
    hasError: !!error,

    // Cache functions
    cacheData,
    getCachedData,

    // Offline functions
    queueForSync,

    // Utility functions
    getMemoryUsage: performanceManager?.getMemoryUsage?.bind(performanceManager) || (() => null),
    getPerformanceReport: performanceManager?.getPerformanceReport?.bind(performanceManager) || (() => ({}))
  };
};

/**
 * Hook for global performance monitoring (no component-specific state)
 */
export const useGlobalPerformance = () => {
  const [globalState, setGlobalState] = useState({
    activeLoading: 0,
    totalErrors: 0,
    memoryStatus: 'optimal',
    isOnline: navigator.onLine
  });

  useEffect(() => {
    const updateGlobalState = () => {
      const report = performanceManager?.getPerformanceReport?.() || {
        loading: { active: 0 },
        errors: { count: 0 },
        offline: { isOnline: navigator.onLine }
      };
      setGlobalState({
        activeLoading: report.loading?.active || 0,
        totalErrors: report.errors?.count || 0,
        memoryStatus: performanceManager?.isMemoryOptimal?.() ? 'optimal' : 'high',
        isOnline: report.offline?.isOnline ?? navigator.onLine
      });
    };

    // Update initially
    updateGlobalState();

    // Listen for global events
    const handleGlobalUpdate = () => updateGlobalState();

    window.addEventListener('performance-loading-start', handleGlobalUpdate);
    window.addEventListener('performance-loading-end', handleGlobalUpdate);
    window.addEventListener('performance-error-tracked', handleGlobalUpdate);
    window.addEventListener('performance-error-cleared', handleGlobalUpdate);
    window.addEventListener('online', handleGlobalUpdate);
    window.addEventListener('offline', handleGlobalUpdate);
    window.addEventListener('memory-optimized', handleGlobalUpdate);

    // Update periodically
    const interval = setInterval(updateGlobalState, 10000); // Every 10 seconds

    return () => {
      window.removeEventListener('performance-loading-start', handleGlobalUpdate);
      window.removeEventListener('performance-loading-end', handleGlobalUpdate);
      window.removeEventListener('performance-error-tracked', handleGlobalUpdate);
      window.removeEventListener('performance-error-cleared', handleGlobalUpdate);
      window.removeEventListener('online', handleGlobalUpdate);
      window.removeEventListener('offline', handleGlobalUpdate);
      window.removeEventListener('memory-optimized', handleGlobalUpdate);
      clearInterval(interval);
    };
  }, []);

  return {
    ...globalState,
    getPerformanceReport: performanceManager?.getPerformanceReport?.bind(performanceManager) || (() => ({}))
  };
};